import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { ArrowLeft, ArrowRight, CheckCircle2, Eye, Heart, Target, Crosshair } from 'lucide-react';
import './StrategyWorkspace.css';

interface StrategySection {
  id: string;
  label: string;
  icon: LucideIcon;
  progress: number;
  href?: string;
}

interface StrategyWorkspaceHeaderProps {
  companyId: string;
  step: number;
  stage: string;
  title: string;
  description: string;
  progress: number;
  progressLabel?: string;
  activeSection: string;
  sections: StrategySection[];
  onSectionChange?: (section: string) => void;
  saveStatus?: ReactNode;
  next?: { label: string; href: string };
}

const percentage = (value: number) =>
  Number.isFinite(value) ? Math.min(100, Math.max(0, Math.round(value))) : 0;

export function foundationSections(
  companyId: string,
  progress: { vision: number; mission: number; values: number; objectives: number }
): StrategySection[] {
  return [
    { id: 'vision', label: 'Vision', icon: Eye },
    { id: 'mission', label: 'Mission', icon: Target },
    { id: 'values', label: 'Values', icon: Heart },
    { id: 'objectives', label: 'Objectives', icon: Crosshair },
  ].map((section) => ({
    ...section,
    progress: progress[section.id as keyof typeof progress],
    href: `/company/${companyId}/strategy/foundation/${section.id}`,
  }));
}

export default function StrategyWorkspaceHeader({
  companyId,
  step,
  stage,
  title,
  description,
  progress,
  progressLabel = 'Stage progress',
  activeSection,
  sections,
  onSectionChange,
  saveStatus,
  next,
}: StrategyWorkspaceHeaderProps) {
  const completion = percentage(progress);

  return (
    <header className="strategy-workspace-header">
      <div className="strategy-workspace-location">
        <a href={`/company/${companyId}/strategy`} className="strategy-workspace-back">
          <ArrowLeft size={15} aria-hidden="true" />
          All planning stages
        </a>
        <p className="strategy-workspace-eyebrow">
          Step {step} of 5 · {stage}
        </p>
      </div>
      <div className="strategy-workspace-intro">
        <div className="strategy-workspace-copy">
          <h1>{title}</h1>
          <p className="strategy-workspace-description">{description}</p>
        </div>
        <div className="strategy-workspace-actions">
          {saveStatus && <div className="strategy-workspace-save">{saveStatus}</div>}
          {next && (
            <a className="strategy-workspace-next" href={next.href} aria-label={next.label}>
              <span className="strategy-workspace-next-label">{next.label}</span>
              <span className="strategy-workspace-next-mobile" aria-hidden="true">
                Next
              </span>
              <ArrowRight size={16} aria-hidden="true" />
            </a>
          )}
        </div>
      </div>
      <div className="strategy-workspace-toolbar">
        <nav
          className={`strategy-workspace-sections section-count-${sections.length}`}
          aria-label={`${stage} sections`}
        >
          {sections.map(({ id, label, icon: Icon, progress: sectionProgress, href }) => {
            const selected = activeSection === id;
            const sectionCompletion = percentage(sectionProgress);
            const content = (
              <>
                <Icon size={16} aria-hidden="true" />
                <span>{label}</span>
                {sectionCompletion === 100 ? (
                  <CheckCircle2
                    className="strategy-workspace-section-complete"
                    size={15}
                    aria-label="Complete"
                  />
                ) : sectionCompletion > 0 ? (
                  <span className="strategy-workspace-section-progress">{sectionCompletion}%</span>
                ) : null}
              </>
            );
            const className = `strategy-workspace-section${selected ? ' is-active' : ''}`;
            return href ? (
              <a
                key={id}
                href={href}
                className={className}
                aria-current={selected ? 'page' : undefined}
                aria-label={`${label}${sectionCompletion === 100 ? ' complete' : sectionCompletion > 0 ? ` ${sectionCompletion}%` : ''}`}
              >
                {content}
              </a>
            ) : (
              <button
                key={id}
                type="button"
                className={className}
                aria-pressed={selected}
                aria-label={`${label}${sectionCompletion === 100 ? ' complete' : sectionCompletion > 0 ? ` ${sectionCompletion}%` : ''}`}
                onClick={() => onSectionChange?.(id)}
              >
                {content}
              </button>
            );
          })}
        </nav>
        <div className="strategy-workspace-progress">
          <div>
            <span>{progressLabel}</span>
            <strong>{completion}%</strong>
          </div>
          <div
            role="progressbar"
            aria-label={progressLabel}
            aria-valuenow={completion}
            aria-valuemin={0}
            aria-valuemax={100}
            className="strategy-workspace-progress-track"
          >
            <span style={{ width: `${completion}%` }} />
          </div>
        </div>
      </div>
    </header>
  );
}
